import { buildProps, definePropType } from '@ll-plus/utils'

import type { ExtractPropTypes } from 'vue'

import Color from './color'

import type ColorPicker from '../color-picker.vue'

export type ComponentSize = 'large' | 'default' | 'small'

export const isValidComponentSize = (val: string): boolean => {
  return ['', 'large', 'default', 'small'].includes(val)
}

export const displayedRgb = (color: Color, showAlpha: boolean) => {
  if (!(color instanceof Color)) {
    throw Error('color should be instance of _color Class')
  }
  const { r, g, b } = color.toRgb()
  return showAlpha
    ? `rgba(${r}, ${g}, ${b}, ${color.get('alpha') / 100})`
    : `rgb(${r}, ${g}, ${b})`
}

export const colorFormatOptions = [
  { label: 'hex', value: 'hex' },
  { label: 'rgb', value: 'rgb' },
  { label: 'rgba', value: 'rgba' },
  { label: 'hsv', value: 'hsv' },
  { label: 'hsl', value: 'hsl' },
]

export type IColorFormat = 'hex' | 'rgb' | 'rgba' | 'hsv' | 'hsl'

export const colorPickerProps = buildProps({
  value: {
    type: String,
  },
  showAlpha: {
    type: Boolean,
    default: true,
  },
  colorFormat: {
    type: definePropType<IColorFormat>(String),
    default: 'hex',
  },
  popperClass: {
    type: String,
  },
  predefine: {
    type: definePropType<Color[]>(Array),
    default: () => [
      '#1187FF',
      '#3BA3FF',
      '#52C41A',
      '#FAAD14',
      '#FF8C00',
      '#FF4D4F',
      '#FF4500',
      '#13C2C2',
      '#531DAB',
      '#C71585',
      '#005ce5',
      '#E6A23C',
      '#1D2129',
      '#272E3B',
      '#4E5969',
      '#86909C',
      '#C9CDD4',
      '#D9D9D9',
      '#F4F4F4',
      '#FAFAFA',
      '#FF4500AD',
      '#90F09080',
      '#1F93FFBA',
      '#C7158575',
      '#000000A6',
      '#00000073',
    ],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: true,
  },
  size: {
    type: definePropType<ComponentSize>(String),
    validator: isValidComponentSize,
  },
  placeholder: {
    type: String,
    default: '请选择',
  },
  // 拾色器模式
  pickerMode: {
    type: Boolean,
    default: false,
  },
  // 是否点击清除图标删除内容
  allowClear: {
    type: Boolean,
    default: true,
  },
  usePipette: {
    type: Boolean,
    default: true,
  },
  pipetteContainer: {
    type: definePropType<HTMLElement>(Object),
    default: () => document.body,
  },
} as const)

export const colorPickerEmits = {
  'update:value': (value: string | null) =>
    typeof value === 'string' || value === null,
  change: (value: string | null) => typeof value === 'string' || value === null,
  'active-change': (value: string | null) =>
    typeof value === 'string' || value === null,
}

// props
export type ColorPickerProps = ExtractPropTypes<typeof colorPickerProps>

// emits
export type ColorPickerEmits = typeof colorPickerEmits

// instance
export type ColorPickerInstance = InstanceType<typeof ColorPicker>
